import type {
  CreateTextConsultationBody,
  UpdateTextConsultationBody,
} from "api/consultationService";
import type { DrpChecklistRow, EducationChecklistRow } from "common/models/types";
import type { DrpDisplayRow, EducationDisplayRow } from "utils/checklistGuidance";
import type { TextRecordPayload } from "./textRecordSaveQueue";

/**
 * 화면 DRP 행 → 요청 행(설계 결정 21). **명시 나열** — 스프레드를 쓰면 화면 전용 `isNew`가 body에 섞여
 * 서버가 `forbidNonWhitelisted`로 400을 낸다. `guidance`는 켜진 행에만 키를 두고(빈 칸 = 키 없음, null 금지),
 * `checked = (guidance === "done")`를 항상 함께 싣는다.
 */
export function toDrpRequestRow(r: DrpDisplayRow): DrpChecklistRow {
  return {
    no: r.no,
    severity: r.severity,
    type: r.type,
    summary: r.summary,
    checked: r.guidance === "done",
    ...(r.guidance ? { guidance: r.guidance } : {}),
  };
}

/** 화면 약사 확인·교육 행 → 요청 행(규칙은 DRP 행과 같다, severity 없음). */
export function toEducationRequestRow(r: EducationDisplayRow): EducationChecklistRow {
  return {
    no: r.no,
    type: r.type,
    summary: r.summary,
    checked: r.guidance === "done",
    ...(r.guidance ? { guidance: r.guidance } : {}),
  };
}

/** 생성 바디(계약 예시 본문과 같은 모양, version 2 — 설계 결정 21). */
export function buildCreateBody(p: TextRecordPayload): CreateTextConsultationBody {
  return {
    patientId: p.patientId,
    context: {
      source: "text",
      version: 2,
      consultedAt: p.consultedAt,
      reviewId: p.reviewId,
      drpSource: p.drpSource,
      drpFormat: p.drpFormat,
      drpItems: p.drpItems,
      educationItems: p.educationItems,
      note: p.note,
    },
  };
}

/** 수정 바디(서버 소유 키 제외 — reviewId·reviewDate·source·version·updatedAt 금지). */
export function buildUpdateBody(p: TextRecordPayload): UpdateTextConsultationBody {
  return {
    drpSource: p.drpSource,
    drpFormat: p.drpFormat,
    drpItems: p.drpItems,
    educationItems: p.educationItems,
    note: p.note,
    consultedAt: p.consultedAt,
  };
}

/** KST(Asia/Seoul) 오늘 YYYY-MM-DD (toISOString 금지 — 00~09시에 하루 전이 된다). */
export function kstToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul" }).format(new Date());
}

/** 미래 날짜 판정(YYYY-MM-DD 문자열 비교, KST 오늘 기준). */
export function isFutureDate(date: string, today: string = kstToday()): boolean {
  return date > today;
}

// 결정 9(2026-10-12 개정): 메모에 날짜 줄 자동 삽입·상담일 자동 변경을 없앴다.
// 이어 쓰기 날짜 줄 함수(applyContinuationDateLines·insertedText·applyFirstEditContinuation)는 폐기했다.
